<template lang="pug">
.other-office-reply-message
  .sub-title.d-flex.align-items-center.mb-2
    //- datetime
    .d-flex.flex-fill
      span.sender-facility.mr-2 {{ getSender(value) }}
      span.send-datetime {{ convertSendDatetime(value) }}
    //- control buttons
    .d-flex.justify-content-end(v-if="value.senderFacilityId == facility.id")
      button.btn.mini-btn.mr-2(v-if="value.deleteFlag == false && value.isEditing == false" @click="onEditReply(value)") 編集
      .buttons(v-else-if="value.deleteFlag == false && value.isEditing == true")
        button.btn.mini-btn.mr-2(@click="onUpdateReply(message, value)") 更新
        button.btn.mini-btn(@click="onUpdateCancel(value)") キャンセル
      button.btn.mini-btn(v-if="value.deleteFlag == false && value.isEditing == false" @click="onDeleteReply(message, value)") 削除

  //- reply text
  .message-text.mb-2(v-if="value.deleteFlag == false && value.isEditing == false") {{ value.text }}
  //- edit reply title
  .title-input.flex-fill(v-else-if="value.deleteFlag == false && value.isEditing == true")
    b-form-textarea.mr-2(v-model="updateText" key-name="" name="reply-text" placeholder="返信本文"
      :validation-rules="{ required: true, max: 1000 }" validationKeyName="返信本文"
      :maxlength="1000" :styles="{ minWidth: '220px', width: '100%', height: '41px' }" required)
    .error-messages.mt-1(v-if="errors.has('reply-text')")
      label.error-message(v-for="error in errors.collect('reply-text')") {{ error }}
  //- delete message
  .message-text.undo-delete(v-if="value.deleteFlag == true && value.isEditing == false")
    span.mr-2 このメッセージは削除されました。
    a.undo-delete-message(v-if="value.senderFacilityId == facility.id" @click="onUndoDeleteReply(message, value)") 元に戻す

  //- files
  .file(v-if="value.deleteFlag == false && value.isEditing == false")
    MessageTempFile.mt-2(v-for="(file, index) in value.fileUrls" v-model="value.fileUrls[index]"
      :id="`${value.datetime}-reply-${index}`" :itemWidth="'60%'" @download="download")
  //- edit files
  .file.mt-2(v-else-if="value.deleteFlag == false && value.isEditing == true")
    .d-flex.mb-2
      DraggableFiles.draggable-files(v-model="updatedFiles" @delete="onFileDelete" :item-width="'90%'")
    .d-flex.flex-column.justify-content-end
      MultiFileSelecter.mr-2(:name="`${value.datetime}-update-temp-files`" :mimetypes="_mimeTypes" :limit="_registerFilesLimit"
        :width="'100%'" :height="'32px'" :showPreview="false" @input="inputFiles")
      .error-messages.text-break(v-for="error in errors.collect(`${value.datetime}-update-temp-files`)")
        label.error-message {{ error }}
        br

  //- loading modal
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";

import { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";
import { FileUrl } from "@/commons/services/OtherOfficeMessages";
import { FacilityGetModel } from "@/commons/services/Facilities";
import S3UrlService from "@/commons/services/S3Url";

import OtherOfficeMessageFileService from "@/commons/services/OtherOfficeMessageFile";
import { OtherOfficeMessageListTabMessage, OtherOfficeMessageListTabMessageReply } from "@/pc/containers/sendOtherOfficeMessages/OtherOfficeMessageListTab.vue";
import { OtherOfficeConnectionTableItem } from "./OtherOfficeConnectionTable.vue";

import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import MessageTempFile from "@/pc/containers/sendOtherOfficeMessages/MessageTempFile.vue";
import MultiFileSelecter from "@/pc/basics/MultiFileSelecter.vue";
import DraggableFiles from "@/pc/components/DraggableFiles.vue";
import DraggableFile from "@/commons/models/DraggableFile";

import PageLoading from "@/pc/basics/PageLoading.vue";

/**
 * ### メッセージの返信コンポーネント
 */
@Component({
  components: {
    FormTextarea,
    MessageTempFile,
    MultiFileSelecter,
    DraggableFiles,
    PageLoading
  },
  $_veeValidate: { validator: "new" }
})
export default class OtherOfficeReplyMessage extends Vue {
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ユーザー名。ユーザー情報修正画面で更新されるので、storeのgetterから値を取得する */
  @Getter("getFacility") public facility!: FacilityGetModel;

  /** 返信情報 */
  @Prop()
  value!: OtherOfficeMessageListTabMessageReply;
  /** メッセージ */
  @Prop()
  message!: OtherOfficeMessageListTabMessage;
  /** 他事業所連携 */
  @Prop()
  item!: OtherOfficeConnectionTableItem;
  /** 添付ファイル最大数 */
  @Prop()
  fileUploadLimit!: number;
  /** 添付許可Content-Typeリスト */
  @Prop()
  mimeTypes!: string[];
  /** アカウント情報MAP（key：userId、value：アカウント情報） */
  @Prop()
  accountMap!: Map<string, StandardAccountsGetModel>;

  /** 更新後本文 */
  updateText: string = "";
  /** 更新後ファイル */
  updatedFiles: Array<DraggableFile> = [];

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  get _mimeTypes(): string[] {
    if (this.mimeTypes) {
      return this.mimeTypes;
    } else {
      return [];
    }
  }

  /** ### 登録可能なファイル数を返す */
  get _registerFilesLimit(): number {
    if (this.updatedFiles && this.fileUploadLimit) {
      const limit: number = this.fileUploadLimit - this.updatedFiles.length;
      return limit;
    } else {
      return 0;
    }
  }

  public resetUpdateReplyFiles(): void {
    this.updatedFiles = [];
    return;
  }

  /**
   * ### 編集ボタン押下イベント
   *
   * - 編集元のメッセージを入力用プロパティに転記する
   * - 編集状態をtrueに切替えてメッセージを編集可能にする
   */
  public async onEditReply(reply: OtherOfficeMessageListTabMessageReply): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 編集元メッセージの情報を転記。ファイルについてはS3から取得してFileオブジェクトにする
    this.updateText = reply.text;
    const files: Array<DraggableFile> = [];
    for (let i = 0; i < reply.fileUrls.length; i++) {
      const file: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, reply.fileUrls[i].url, reply.fileUrls[i].name);
      const draggableFile: DraggableFile = file;
      draggableFile.key = i.toString();
      files.push(draggableFile);
    }
    this.updatedFiles = files;
    // 編集中に状態変更
    reply.isEditing = !reply.isEditing;

    this.isBusy = false;

    return;
  }

  /**
   * ### 編集内容の更新ボタン押下イベント
   *
   * - バリデーションしてエラーがあれば何もしない
   * - エラーがなければ親コンポーネントにメッセージと編集内容を通知する
   */
  public async onUpdateReply(message: OtherOfficeMessageListTabMessage, reply: OtherOfficeMessageListTabMessageReply): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.$emit("onUpdateReply", { message, reply, input: { text: this.updateText, files: this.updatedFiles } });
    }
    return;
  }

  /**
   * ### 編集内容のキャンセルボタン押下イベント
   *
   * - 入力内容を初期化
   * - 編集状態をfalseに切替えて編集不可にする
   */
  public onUpdateCancel(reply: OtherOfficeMessageListTabMessageReply): void {
    this.updateText = "";
    this.updatedFiles = [];
    reply.isEditing = false;
    return;
  }

  /** ### 削除ボタン押下イベントを親コンポーネントに通知する */
  public onDeleteReply(deleteTargetMessage: OtherOfficeMessageListTabMessage, reply: OtherOfficeMessageListTabMessageReply): void {
    this.$emit("onDeleteReply", { deleteTargetMessage, reply });
    return;
  }

  /** ### 元に戻す押下イベントを親コンポーネントに通知する */
  public onUndoDeleteReply(undoDeleteTargetMessage: OtherOfficeMessageListTabMessage, reply: OtherOfficeMessageListTabMessageReply): void {
    this.$emit("onUndoDeleteReply", { undoDeleteTargetMessage, reply });
    return;
  }

  /**
   * ### 投稿者を取得する
   */
  public getSender(reply: OtherOfficeMessageListTabMessageReply): string {
    if (!this.item) {
      return "";
    }
    if (reply.senderFacilityId == this.item.registerdFacilityId) {
      return this.item.officeName;
    } else {
      const latestUserName: string | undefined = this.accountMap.get(reply.userId)?.name;
      const userName: string = reply.userName ? reply.userName : latestUserName ? latestUserName : "";
      return `${userStore.facility.name}（${userName}）`;
    }
  }

  /**
   * ### 日付数値を日付文字列に変換する
   */
  public convertSendDatetime(reply: OtherOfficeMessageListTabMessageReply): string {
    if (!reply) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(reply.datetime, "yyyy/mm/dd hh:MM");
  }

  /** ### ダウンロードボタン押下イベントを親コンポーネントに通知する */
  public download(fileUrl: FileUrl): void {
    this.$emit("download", fileUrl);
    return;
  }

  public async inputFiles(files: Array<File>): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const service: OtherOfficeMessageFileService = new OtherOfficeMessageFileService(this.$validator, `${this.value.datetime}-update-temp-files`);
    const addFiles: Array<File> = await service.createAddFile(files, this._registerFilesLimit, this.updatedFiles.length);
    if (0 < addFiles.length) {
      this.updatedFiles = this.updatedFiles.concat(addFiles);

      for (let i = 0; i < this.updatedFiles.length; i++) {
        this.updatedFiles[i].key = i.toString();
      }
    }

    this.isBusy = false;
    return;
  }

  /**
   * ### ファイル削除イベント
   */
  public onFileDelete(index: number): void {
    this.updatedFiles.splice(index, 1);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.other-office-reply-message {
  .sender-facility {
    font-weight: bold;
  }
  .sender-facility,
  .send-datetime {
    color: $date-font-color;
    text-align: right;
    font-size: 14px;
  }
  .mini-btn {
    height: 32px !important;
    font-size: 14px !important;
  }
  .undo-delete {
    font-weight: bold;
    font-size: 16px;
  }
  .undo-delete-message {
    cursor: pointer;
    font-weight: bold;
  }
  .message-text {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  ::v-deep {
    .form-control {
      color: #000;
    }
    .noinput {
      background-color: $bg-require-input !important;
      &::placeholder {
        color: rgb(117, 117, 117);
      }
    }
  }
}
</style>
